import { useToggle } from '@root/hooks/useToggle';
import { Layout, Text, PopoverContent, Anchor, Sep } from '@root/styles/Design';
import { useMemo } from 'react';
import { Popover } from 'react-tiny-popover';
import { CodeEditor } from './CodeEditor';
import { EndpointTesterResponse } from './Models';

export function ResponseViewer({ response }: { response: null | EndpointTesterResponse }) {
    const { appHeaders, appDur, rateLimit } = useAppHeaders(response?.headers || {});
    const fmtInt = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });

    if (response === null) {
        return (
            <Layout vert fill justify="center" align="center">
                <Text color="text-dim">Loading...</Text>
            </Layout>
        );
    } else if (response.status <= 0) {
        return (
            <Layout vert fill justify="center" align="center">
                {response.status < 0 ? (
                    <Text color="text-dim">{response.body}</Text>
                ) : (
                    <Text color="text-dim">Send a request to see the response.</Text>
                )}
            </Layout>
        );
    } else {
        return (
            <Layout noScroll vert fill>
                <Layout horz px={16} p={8} gap="md" justify="start" align="end">
                    <Layout vert align="start">
                        <Text size="sm" color="text-dim">
                            Status
                        </Text>
                        <Layout horz gap="sm">
                            <Text size="md" color={response.status < 400 ? 'success' : 'danger'} weight="bold">
                                {response?.status}
                            </Text>
                            <AppHeadersLink appHeaders={appHeaders} />
                        </Layout>
                    </Layout>
                    <Sep vert />
                    <Layout vert align="start">
                        <Text size="xs" color="text-dim">
                            Duration
                        </Text>
                        <Text size="md">{`e2e: ${fmtInt.format(response.durationMs)}ms${appDur ? `, Server: ${fmtInt.format(appDur)}ms` : ''}`}</Text>
                    </Layout>
                    {!rateLimit ? null : (
                        <>
                            <Sep vert />
                            <Layout vert align="start">
                                <Text size="xs" color="text-dim">
                                    Rate Limit
                                </Text>
                                <Text size="md">{`${rateLimit.remaining} remaining; reset in ${rateLimit.reset}sec`}</Text>
                            </Layout>
                        </>
                    )}
                </Layout>
                <Text px={16} pb={4} size="sm" color="text-dim">
                    Response Body
                </Text>
                <Sep horz />
                {!response.body ? (
                    <Layout vert fill align="center" justify="center">
                        <Text color="text-dim" italic>
                            Empty
                        </Text>
                    </Layout>
                ) : (
                    <Layout fill>
                        <CodeEditor value={response.body} readonly />
                    </Layout>
                )}
            </Layout>
        );
    }
}

function AppHeadersLink({ appHeaders }: { appHeaders: { key: string; value: string }[] }) {
    const [headersVisible, { toggle: toggleHeaders, setOff: hideHeaders }] = useToggle(false);
    return !appHeaders.length ? (
        <></>
    ) : (
        <Popover
            isOpen={headersVisible}
            positions={['bottom']}
            onClickOutside={hideHeaders}
            content={
                <PopoverContent>
                    <Layout vert gap="sm">
                        {appHeaders.map(({ key, value }) => (
                            <Text key={key} mono>
                                {key}: {value}
                            </Text>
                        ))}
                    </Layout>
                </PopoverContent>
            }
        >
            <Anchor size="md" onClick={toggleHeaders}>
                View Headers
            </Anchor>
        </Popover>
    );
}

function useAppHeaders(headers: Record<string, string>) {
    return useMemo(() => {
        const parseHeaderValue = (value: string) => {
            const rawItems = value.split(',').map((item) => item.trim());
            const result: Array<{ name: string; map: Record<string, string> }> = [];
            for (const rawItem of rawItems) {
                const [name, ...details] = rawItem.split(';').map((p) => p.trim());
                const detailMap = details.reduce(
                    (acc, detail) => {
                        const [key, value] = detail.split('=');
                        if (key && value) {
                            acc[key] = value;
                        }
                        return acc;
                    },
                    {} as Record<string, string>,
                );
                result.push({ name, map: detailMap });
            }
            return result;
        };
        const appHeaders: Array<{ key: string; value: string; lcKey: string }> = Object.entries(headers)
            .map(([key, value]) => ({ key, value, lcKey: key.toLowerCase() }))
            .filter(({ lcKey }) => lcKey.startsWith('ratelimit-') || lcKey === 'server-timing');
        const limitValues = ['limit', 'remaining', 'reset'].map(
            (suffix) => parseInt(appHeaders.find((h) => h.lcKey === `ratelimit-${suffix}`)?.value ?? '') ?? NaN,
        );
        const [limit, remaining, reset] = limitValues;
        const rateLimit = limitValues.some(isNaN) ? null : { limit, remaining, reset };

        const rawTimingHeader = 'server-timing' in headers ? headers['server-timing'] : '';
        const rawTimings = parseHeaderValue(rawTimingHeader);
        const rawAppDur = rawTimings.find((t) => t.name === 'ttfb')?.map['dur'];
        const appDur = !rawAppDur ? null : parseFloat(rawAppDur);

        return { appHeaders, appDur, rateLimit };
    }, [headers]);
}
